import React from "react";

import { Chip, Icon } from "@performyard/py-components";
import type { TemplateLibraryEntrySummary } from "@performyard/service-contracts";

import { TYPE_META } from "../type-meta";

type Props = {
	entry: TemplateLibraryEntrySummary;
	selected: boolean;
	onSelect: (id: string) => void;
};

export const TemplateLibraryListItem = ({ entry, selected, onSelect }: Props) => {
	const meta = TYPE_META[entry.type];
	return (
		<button
			type="button"
			role="option"
			aria-selected={selected}
			onClick={() => onSelect(String(entry._id))}
			className={[
				"relative w-full appearance-none border-0 p-4 text-left",
				"focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary-5",
				"transition-colors duration-150 motion-reduce:transition-none",
				selected ? "bg-primary-1" : "bg-neutral-0 hover:bg-neutral-1",
			].join(" ")}
		>
			<div className="flex items-start gap-3">
				<div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-neutral-1">
					<Icon icon={meta.icon} color={selected ? "primary-5" : "neutral-7"} size={20} />
				</div>

				<div className="min-w-0 flex-1">
					<div className="flex items-start justify-between gap-3">
						<div className="min-w-0">
							<div className="text-paragraph-3 font-semi-bold break-words text-neutral-9">
								{entry.name}
							</div>
							<div className="mt-2 py-line-clamp-2 text-paragraph-3 text-neutral-7">
								{entry.description}
							</div>
						</div>
						<Chip label={meta.short} color="neutral" size={24} />
					</div>

					{entry.tags.length > 0 && (
						<div className="mt-3 flex flex-wrap items-center gap-2">
							{entry.tags.slice(0, 3).map((tag) => (
								<span
									key={tag}
									className="inline-flex h-5 items-center rounded-full bg-neutral-2 px-2 text-caption text-neutral-7"
								>
									{tag}
								</span>
							))}
							{entry.tags.length > 3 && (
								<span className="text-caption text-neutral-7">+{entry.tags.length - 3}</span>
							)}
						</div>
					)}
				</div>
			</div>
		</button>
	);
};

export default TemplateLibraryListItem;
